import { useQuery } from '@tanstack/react-query';
import { fetchWithTimeout } from '../lib/kiosk-stability';

interface WeatherData {
  tempMin: number;
  tempMax: number;
  weatherCode: number;
  description: string;
  icon: string; // emoji
}

// Map WMO weather codes to emoji + description
function mapWeatherCode(code: number): { icon: string; description: string } {
  if (code === 0) return { icon: '☀️', description: 'Clear' };
  if (code <= 3) return { icon: '⛅', description: 'Partly cloudy' };
  if (code <= 48) return { icon: '🌫️', description: 'Foggy' };
  if (code <= 57) return { icon: '🌧️', description: 'Drizzle' };
  if (code <= 67) return { icon: '🌧️', description: 'Rain' };
  if (code <= 77) return { icon: '❄️', description: 'Snow' };
  if (code <= 82) return { icon: '🌦️', description: 'Showers' };
  if (code <= 99) return { icon: '⛈️', description: 'Thunderstorm' };
  return { icon: '🌤️', description: 'Unknown' };
}

export function useWeather(lat = -37.8136, lon = 144.9631, timezone = 'Australia/Melbourne') {
  return useQuery<WeatherData>({
    queryKey: ['weather', lat, lon],
    queryFn: async () => {
      const res = await fetchWithTimeout(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max,temperature_2m_min,weather_code&timezone=${encodeURIComponent(timezone)}&forecast_days=1`,
        {},
        10000
      );
      const data = await res.json();
      const { icon, description } = mapWeatherCode(data.daily.weather_code[0]);
      return {
        tempMin: Math.round(data.daily.temperature_2m_min[0]),
        tempMax: Math.round(data.daily.temperature_2m_max[0]),
        weatherCode: data.daily.weather_code[0],
        description,
        icon,
      };
    },
    staleTime: 30 * 60 * 1000, // 30 min cache
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
    refetchOnWindowFocus: false,
  });
}